import { parseISO } from 'date-fns';
import { action, computed, makeObservable, observable } from 'mobx';
import { EligibilityRule, EligibilityRuleType, ProgramDate, ProgramDateTypeCode, ProgramRef } from '../models';
import { isWindowActive } from '../shared/functions';

export class AwardStore {
  @observable programDates: ProgramDate[] = [];

  @observable defaultProgramRef?: ProgramRef;

  @observable eligibilityRules: Map<EligibilityRuleType, EligibilityRule[]> = new Map();

  constructor() {
    makeObservable(this);
  }

  @action
  setProgramDates(programDates: ProgramDate[]): void {
    this.programDates = programDates;
  }

  @action
  setDefaultProgramRef(programRef: ProgramRef): void {
    this.defaultProgramRef = programRef;
  }

  @action
  setEligibilityRules(type: EligibilityRuleType, eligibilityRules: EligibilityRule[]): void {
    this.eligibilityRules.set(type, eligibilityRules);
  }

  getEligibilityRules(type: EligibilityRuleType): EligibilityRule[] {
    return this.eligibilityRules.get(type) ?? [];
  }

  @computed
  get nominationWindowProgramDate(): ProgramDate | undefined {
    return this.programDates.find((programDate) => programDate.dateTypeCode === 'Nomination-Window');
  }

  private hasActiveWindow(dateTypeCode: ProgramDateTypeCode): boolean {
    const programDate = this.programDates.find(value => value.dateTypeCode === dateTypeCode);
    return programDate !== undefined && isWindowActive(programDate);
  }

  @computed
  get isNominationWindowOpen(): boolean {
    return this.hasActiveWindow('Nomination-Window');
  }

  @computed
  get isScrubbingWindowOpen(): boolean {
    return this.hasActiveWindow('Scrubbing-Window');
  }

  @computed
  get isJudgingWindowOpen(): boolean {
    return this.hasActiveWindow('Judging-Window');
  }

  @computed
  get isReviewAndDesignateWindowOpen(): boolean {
    return this.hasActiveWindow('Review-&-Designate-Window');
  }

  @computed
  get isComplianceAndRevisionsWindowOpen(): boolean {
    return this.hasActiveWindow('Compliance-&-Revisions-Window');
  }

  @computed
  get isWinnersWindowOpen(): boolean {
    return this.hasActiveWindow('Winners-Window');
  }

  @computed
  get isWinnersWindowAlreadyOpen(): boolean {
    const winnerProgramDate = this.programDates.find((programDate) => programDate.dateTypeCode === 'Winners-Window');
    if (!winnerProgramDate) return false;

    return new Date() >= parseISO(winnerProgramDate.startDate);
  }

}
